import React, { useState } from "react";
import { Form, Formik } from "formik";
import { Col, Row, Button } from "react-bootstrap";
import {
  getFormFields,
  newVendorCorpName,
  newVendorMerchantFields,
  newVendorAddressFields,
  newVendorContactFields,
  getInitialFormValues,
  getFormValidations,
  formatPhone,
} from "./formFields";
import axios from "../../../../service/axios";
import OnSubmitErrorMessage from "../../../Common/Errors/OnSubmitErrorMessage";

import * as RolePermissionConstants from "../../../../constants/RolePermissionConstants";
import { getUserRolePermission } from "../../../../utils/rolePermissionAccess";
import "./AddVendor.css";
import Cookies from "js-cookie";
import * as Constants from "../../../../constants/Constants";
import { useDispatch } from "react-redux";
import { resetServiceData } from "../../../../redux/serviceSlice";
import { resetCommonSliceData } from "../../../../redux/commonSlice";

const validationRules = getFormValidations([
  ...newVendorCorpName,
  ...newVendorMerchantFields,
  ...newVendorAddressFields,
  ...newVendorContactFields,
]);

const initialFormValues = getInitialFormValues([
  ...newVendorCorpName,
  ...newVendorMerchantFields,
  ...newVendorAddressFields,
  ...newVendorContactFields,
]);

const AddVendor = () => {
  const [submitError, setSubmitError] = useState({
    error: false,
    message: "",
    type: "",
  });
  const [addressError, setAddressError] = useState("");

  const resetAddVendorForm = (e, resetForm) => {
    e.preventDefault();
    resetForm();
    setSubmitError({ error: false, message: "", type: "" });
  };

  const dispatch = useDispatch();

  return (
    <>
      <Col sm={12} md={12} lg={12} xl={12}>
        {submitError.error && (
          <OnSubmitErrorMessage type="error" message={submitError.message} />
        )}
      </Col>
      <Col sm={12} md={12} lg={10} xl={10}>
        <Formik
          initialValues={initialFormValues}
          validationSchema={validationRules}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            setSubmitError({ error: false, message: "", type: "" });

            let vendorDetails = {
              vendorCorpName: values.vendorCorpName,
              merchantFirstName: values.merchantFirstName,
              merchantLastName: values.merchantLastName,
              merchantEmail: values.merchantEmail,
              vendorInfo: {
                isPrimaryVendor: true,
                streetAddress: values.streetAddress,
                suiteUnitFloor: values.suiteUnitFloor,
                city: values.city,
                state: values.state,
                postalCode: values.postalCode,
                country: values.country,
                firstName: values.firstName,
                lastName: values.lastName,
                corpEmailId: values.corpEmailId,
                phoneNumber: values.phoneNumber,
                extension: values.extension,
                vendorType: values.vendorType ? values.vendorType : null,
                vendorAddType:
                  Cookies.get(Constants.USER_TYPE) === Constants.INTERNAL_UI
                    ? Constants.INTERNAL_BACK_END
                    : Cookies.get(Constants.USER_TYPE) === Constants.EXTERNAL_UI
                    ? Constants.EXTERNAL_BACK_END
                    : "",
              },
            };
            axios()
              .post("/vendorProfile/addVendorByMerchant", vendorDetails)
              .then((res) => {
                if (res.status === 201 && res.data.vendorProfileId) {
                  dispatch(resetServiceData());
                  dispatch(resetCommonSliceData());
                  localStorage.setItem("vid", res.data.vendorProfileId);
                  window.location = `/boarding/vendor/company/${res.data.vendorProfileId}`;
                } else {
                  const message = res?.data
                    ? res.data?.error
                      ? res.data?.error?.message
                      : res.data?.message
                    : "Unable to create a vendor.";
                  const type = message.includes(
                    Constants.ADD_VENDOR_SERVER_ERROR_MSG_MAP.corpEmailId
                  )
                    ? Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.corpEmailId
                    : message.includes(
                        Constants.ADD_VENDOR_SERVER_ERROR_MSG_MAP.vendorCorpName
                      )
                    ? Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.vendorCorpName
                    : Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.other;
                  setSubmitError({
                    error: true,
                    message,
                    type,
                  });
                  setSubmitting(false);
                }
              })
              .catch(({ response }) => {
                const message = response?.data
                  ? response.data?.error
                    ? response.data?.error?.message
                    : response.data?.message
                  : "Unable to create a vendor.";
                const type = message.includes(
                  Constants.ADD_VENDOR_SERVER_ERROR_MSG_MAP.corpEmailId
                )
                  ? Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.corpEmailId
                  : message.includes(
                      Constants.ADD_VENDOR_SERVER_ERROR_MSG_MAP.vendorCorpName
                    )
                  ? Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.vendorCorpName
                  : Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.other;
                setSubmitError({
                  error: true,
                  message,
                  type,
                });
                setSubmitting(false);
              });
          }}
          render={({
            isSubmitting,
            isValid,
            dirty,
            setFieldValue,
            values,
            resetForm,
          }) => (
            <Form
              data-testid="addNewVendor_form"
              onChange={(e) => {
                const fieldName = e.target.name;
                const fieldValue = e.target.value;
                if (
                  fieldName === "state" ||
                  fieldName === "postalCode" ||
                  fieldName === "country"
                ) {
                  setFieldValue(fieldName, fieldValue.toUpperCase(), true);
                }
                if (fieldName === "phoneNumber") {
                  setFieldValue(fieldName, formatPhone(fieldValue), true);
                }

                if (
                  Constants.ADD_VENDOR_SERVER_ERROR_TYPES.includes(fieldName) &&
                  submitError.type === fieldName
                ) {
                  setSubmitError({ error: false, message: "", type: "" });
                }
                if (
                  submitError.type ===
                  Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.other
                ) {
                  setSubmitError({ error: false, message: "", type: "" });
                }
              }}
            >
              <Row>
                {getFormFields(newVendorCorpName).map((field) => field)}
              </Row>
              <Row>
                {getFormFields(newVendorMerchantFields).map((field) => field)}
              </Row>
              <Row>
                {getFormFields(
                  newVendorAddressFields,
                  setFieldValue,
                  values,
                  setAddressError,
                  addressError
                ).map((field) => field)}
              </Row>
              <Row>
                {getFormFields(newVendorContactFields).map((field) => field)}
              </Row>
              <Row>
                <Col xs={12}>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      style={{ marginRight: 20 }}
                      className="button-later-md"
                      disabled={isSubmitting || !dirty}
                      onClick={(e) => resetAddVendorForm(e, resetForm)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="button-primary-md"
                      type="submit"
                      disabled={
                        !getUserRolePermission(
                          RolePermissionConstants.VENDORPLATFORM_MYDASHBOARD_ADDVENDOR,
                          RolePermissionConstants.VMP_ADDVENDOR
                        ) === RolePermissionConstants.EDIT ||
                        !isValid ||
                        !dirty ||
                        isSubmitting
                      }
                    >
                      Add Vendor
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        />
      </Col>
    </>
  );
};

export default AddVendor;
